import { X } from 'lucide-react';
import { getLogoPathFromModel, getLogoAltFromModel } from '../../utils/providerLogos';
import { formatDate, getUserTimezone } from '../../utils/dateFormat';
import type { CaseHistoryEntry, TokenUsageEntry } from '../../types';

// Provider logo component using centralized utility
const ProviderLogo = ({ provider }: { provider: string }) => (
  <img
    src={getLogoPathFromModel(provider)}
    alt={getLogoAltFromModel(provider)}
    className="w-4 h-4 object-contain flex-shrink-0 dark:brightness-0 dark:invert"
  />
);

interface CaseHistoryPanelProps {
  historyData: CaseHistoryEntry[];
  historyLoading: boolean;
  tokenUsageData: TokenUsageEntry[];
  tokenUsageLoading: boolean;
  onClose: () => void;
  onViewDiff: (diff: string) => void;
}

export function CaseHistoryPanel({
  historyData,
  historyLoading,
  tokenUsageData,
  tokenUsageLoading,
  onClose,
  onViewDiff,
}: CaseHistoryPanelProps) {
  return (
    <div className="mb-4 bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-lg shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-dark-border">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Case History</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">{getUserTimezone()}</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 divide-x divide-gray-100 dark:divide-dark-border">
        {/* Left: Status History */}
        <div className="px-3 py-2 max-h-48 overflow-y-auto">
          <div className="text-xs font-medium text-gray-500 mb-2">Case History</div>
          {historyLoading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
              <span className="inline-block w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
              Loading...
            </div>
          ) : historyData.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm py-2">No history recorded.</p>
          ) : (
            <div className="relative">
              {/* Vertical timeline line */}
              <div className="absolute left-[3px] top-2 bottom-2 w-px bg-gray-300 dark:bg-gray-600" />
              <div className="space-y-1">
                {historyData.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-2 relative">
                    <div className={`w-[7px] h-[7px] mt-[5px] rounded-full flex-shrink-0 z-10 ring-2 ring-gray-50 dark:ring-dark-elevated ${entry.event_type === 'html_edit' ? 'bg-amber-500' : 'bg-purple-500'}`} />
                    <div className="flex-1 min-w-0 py-0.5">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                        {entry.event_type === 'html_edit' ? (
                          <>
                            <span className="font-medium text-gray-800 dark:text-gray-200">HTML edited</span>
                            {entry.notes && (
                              <span className="text-gray-500 dark:text-gray-400">({entry.notes})</span>
                            )}
                            {entry.diff_content && (
                              <button
                                onClick={() => onViewDiff(entry.diff_content!)}
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                View
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="font-medium text-gray-800 dark:text-gray-200">
                            {entry.from_status ? `${entry.from_status} → ${entry.to_status}` : entry.to_status}
                          </span>
                        )}
                        <span className="text-gray-400 dark:text-gray-500">
                          {formatDate(entry.changed_at)}
                        </span>
                      </div>
                      {entry.event_type === 'status_change' && entry.notes && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 break-words">{entry.notes}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Translation History */}
        <div className="px-3 py-2 max-h-48 overflow-y-auto">
          <div className="text-xs font-medium text-gray-500 mb-2">Translation History</div>
          {tokenUsageLoading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
              <span className="inline-block w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
              Loading...
            </div>
          ) : tokenUsageData.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm py-2">No translations recorded.</p>
          ) : (
            <div className="relative">
              {/* Vertical timeline line */}
              <div className="absolute left-[3px] top-2 bottom-2 w-px bg-gray-300 dark:bg-gray-600" />
              <div className="space-y-1">
                {tokenUsageData.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-2 relative">
                    <div className="w-[7px] h-[7px] mt-[5px] rounded-full bg-green-500 flex-shrink-0 z-10 ring-2 ring-gray-50 dark:ring-dark-elevated" />
                    <div className="flex-1 min-w-0 py-0.5">
                      <div className="flex items-center gap-1.5 text-xs">
                        <ProviderLogo provider={entry.provider} />
                        <span className="font-medium text-gray-800 dark:text-gray-200">{entry.provider}</span>
                        <span className="text-gray-500 dark:text-gray-400">·</span>
                        <span className="text-gray-600 dark:text-gray-300">{entry.model}</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        <span className="font-bold">Token Usage:</span>{' '}
                        Input: {entry.input_tokens.toLocaleString()} | Output: {entry.output_tokens.toLocaleString()} | Total: {entry.total_tokens.toLocaleString()}
                      </div>
                      {entry.prompt_used && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          <span className="font-bold">Prompt:</span> {entry.prompt_used}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {formatDate(entry.logged_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
